import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Car, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function RegisterParent() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", password: "" });
  const [loading, setLoading] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(form);
      toast.success("Account created! Let's add your student.");
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create account");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md space-y-8">
        <div className="flex flex-col items-center gap-3 text-center">
          <div className="h-12 w-12 rounded-2xl bg-[hsl(var(--role-parent))] text-white flex items-center justify-center">
            <Car className="h-6 w-6" />
          </div>
          <div>
            <h1 className="font-heading text-2xl font-semibold">Create your parent account</h1>
            <p className="text-sm text-muted-foreground mt-1">Manage lessons, forms, and payments for your student</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="bg-card border rounded-[var(--radius)] p-6 sm:p-8 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="reg-name">Full name</Label>
            <Input id="reg-name" required value={form.full_name} onChange={update("full_name")} className="h-11" data-testid="register-name-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="reg-email">Email address</Label>
            <Input id="reg-email" type="email" required value={form.email} onChange={update("email")} className="h-11" data-testid="register-email-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="reg-phone">Phone number</Label>
            <Input id="reg-phone" type="tel" value={form.phone} onChange={update("phone")} className="h-11" data-testid="register-phone-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="reg-password">Password</Label>
            <Input id="reg-password" type="password" required minLength={6} value={form.password} onChange={update("password")} className="h-11" data-testid="register-password-input" />
          </div>
          <Button type="submit" className="w-full h-11 bg-[hsl(var(--role-parent))] hover:opacity-95" disabled={loading} data-testid="register-submit-button">
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Create account
          </Button>
          <p className="text-sm text-center text-muted-foreground">
            Already have an account?{" "}
            <Link to="/login" className="text-[hsl(var(--role-parent))] font-medium" data-testid="register-login-link">
              Sign in
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
